import type { GrowthPoint } from '@floorpoll/shared';

const WIDTH = 380;
const HEIGHT = 128;
const BASELINE = 100;
const TOP = 16;
const LEFT = 26;
const RADIUS = 4;

/** Rounded at the data end, square at the baseline — the mark spec. */
function columnPath(x: number, width: number, top: number): string {
  const r = Math.min(RADIUS, width / 2, BASELINE - top);
  return `M${x},${BASELINE} L${x},${top + r} Q${x},${top} ${x + r},${top} L${x + width - r},${top} Q${x + width},${top} ${x + width},${top + r} L${x + width},${BASELINE} Z`;
}

/**
 * A single-series column chart.
 *
 * Two measures of different scale get two of these, never one chart with two
 * y-axes — a dual axis is the single most common charting error.
 */
export function UsageColumns({
  points,
  title,
  caption,
  unit,
}: {
  points: GrowthPoint[];
  title: string;
  caption: string;
  unit: string;
}) {
  const max = Math.max(1, ...points.map((point) => point.count));
  // Round the axis up to something clean rather than to the raw maximum.
  const ceiling = Math.max(1, Math.ceil(max / 4) * 4);
  const slot = (WIDTH - LEFT - 12) / Math.max(1, points.length);
  const barWidth = Math.min(24, slot * 0.66);
  const last = points[points.length - 1];

  return (
    <div className="panel">
      <h2 className="panel-title">{title}</h2>
      <p className="panel-caption">{caption}</p>
      <svg
        className="usage-chart"
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        role="img"
        aria-label={`${title}. ${points.map((p) => `week of ${p.weekStart}: ${p.count} ${unit}`).join('; ')}`}
      >
        {[0, 0.5, 1].map((fraction) => {
          const y = BASELINE - fraction * (BASELINE - TOP);
          return (
            <g key={fraction}>
              <line className="usage-grid" x1={LEFT} y1={y} x2={WIDTH} y2={y} />
              <text className="usage-tick" x={LEFT - 6} y={y + 3} textAnchor="end">
                {Math.round(fraction * ceiling)}
              </text>
            </g>
          );
        })}

        {points.map((point, index) => {
          const x = LEFT + 6 + index * slot;
          const height = (point.count / ceiling) * (BASELINE - TOP);
          return (
            <path
              key={point.weekStart}
              className="usage-bar"
              d={columnPath(x, barWidth, BASELINE - height)}
            >
              <title>{`Week of ${point.weekStart} · ${point.count} ${unit}`}</title>
            </path>
          );
        })}

        {last ? (
          <text className="usage-value" x={WIDTH - 14} y={12} textAnchor="middle">
            {last.count}
          </text>
        ) : null}
        <text className="usage-tick" x={LEFT} y={117} textAnchor="start">
          12 wks ago
        </text>
        <text className="usage-tick" x={WIDTH - 14} y={117} textAnchor="middle">
          now
        </text>
      </svg>
    </div>
  );
}
